import React from 'react';
import './StupidSystemDesigns.css';

const battleshipDesignCode = String.raw`enum GameState {
    OPEN,
    BUILDING,
    IN_PROGRESS,
    COMPLETED
};

enum Orientation {
    HORIZONTAL,
    VERTICAL,
    DIAGONAL_DOWN,
    DIAGONAL_UP
};

createGame(player1, player2):
    if players are invalid or identical:
        return failure

    game = new Game(player1, player2)
    games[game.id] = game

    return game.id

makeMove(gameId, playerId, row, col):
    if gameId does not exist:
        return failure

    return games[gameId].makeMove(playerId, row, col)

initialize():
    board1 = new Board()
    board2 = new Board()
    state = BUILDING
    currentPlayerId = player1.id
    winnerId = null

placeShip(playerId, row, col, orientation):
    if state != BUILDING:
        return failure

    board = getBoard(playerId)
    return board.placeShip(row, col, orientation)

setReady(playerId):
    mark player as ready

    if both players are ready
       and both have placed 3 ships:
        state = IN_PROGRESS

    return success

makeMove(playerId, row, col):
    if state != IN_PROGRESS:
        return failure

    if playerId != currentPlayerId:
        return failure

    opponentBoard = getOpponentBoard(playerId)
    result = opponentBoard.receiveAttack(row, col)

    if result == INVALID:
        return failure

    if opponentBoard.allShipsSunk():
        state = COMPLETED
        winnerId = playerId
        return WIN

    currentPlayerId = getOpponent(playerId).id
    return result

checkComplete():
    if board1.allShipsSunk() or board2.allShipsSunk():
        state = COMPLETED
        return true

    return false

placeShip(row, col, orientation):
    positions = calculateShipPositions(row, col, orientation, length=3)

    if any position is outside the board:
        return failure

    if any position already contains a ship:
        return failure

    create ship using positions
    mark each position with shipId
    add ship to ships
    return success

receiveAttack(row, col):
    if coordinates are invalid:
        return INVALID

    cell = grid[row][col]

    if cell.isAttacked:
        return INVALID

    cell.isAttacked = true

    if cell contains a ship:
        increment that ship's hitCount
        return HIT

    return MISS

allShipsSunk():
    for each ship in ships:
        if not ship.isSunk():
            return false

    return true

isSunk():
    return hitCount == 3`;

function BattleshipDesign({ theme, onBackClick }) {
    return (
        <div className={`app ${theme}`}>
            <main className="system-design-page">
                <section className="system-designs-header-section">
                    <div className="system-designs-header">
                        <div className="system-designs-header-copy">
                            <h1>Battleship Low Level Design</h1>
                            <p className="system-designs-subtitle">
                                Model ship placement, turn-based attacks, and independent Battleship matches.
                            </p>
                        </div>
                        <button className="back-btn" onClick={onBackClick}>Back to Designs</button>
                    </div>
                </section>

                <section className="system-designs-content">
                    <div className="system-design-article">
                        <div className="design-section">
                            <h2>Requirements</h2>
                            <ul>
                                <li>Two players each have a 10 × 10 board.</li>
                                <li>Each player places 3 ships, each occupying 3 consecutive cells horizontally, vertically, or diagonally.</li>
                                <li>Players declare themselves ready after placing their ships.</li>
                                <li>Players take turns attacking cells on the opponent&apos;s board.</li>
                                <li>The first player to sink all opponent ships wins.</li>
                                <li>The game ends when a winner is determined or a draw occurs.</li>
                                <li>Multiple games must be supported simultaneously.</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>Core Entities</h2>
                            <ul>
                                <li>
                                    <strong>GameController:</strong> Manages multiple games and players.
                                    <ul>
                                        <li>Fields: <code>games: Map&lt;GameId, Game&gt;</code>, <code>players: Map&lt;PlayerId, Player&gt;</code>.</li>
                                        <li>Methods: <code>createGame</code>, <code>getGame</code>, <code>makeMove</code>, <code>placeShip</code>.</li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Game:</strong> Manages one game&apos;s state and rules.
                                    <ul>
                                        <li>Fields: <code>player1</code>, <code>player2</code>, <code>board1</code>, <code>board2</code>, <code>state</code>, <code>currentPlayerId</code>, <code>winnerId</code>.</li>
                                        <li>Methods: <code>startGame</code>, <code>placeShip</code>, <code>setReady</code>, <code>makeMove</code>, <code>checkComplete</code>.</li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Board:</strong> Holds <code>grid: Cell[10][10]</code> and <code>ships: List&lt;Ship&gt;</code>.
                                    Its methods place ships, receive attacks, and determine whether all ships have sunk.
                                </li>
                                <li><strong>Cell:</strong> Stores a nullable <code>shipId</code> and whether the cell has been attacked.</li>
                                <li><strong>Ship:</strong> Stores <code>cells: List&lt;CellPosition&gt;</code> and <code>hitCount</code>; <code>isSunk</code> is true when the hit count reaches 3.</li>
                            </ul>
                            <p className="system-design-description">
                                <strong>GameState:</strong> OPEN, BUILDING, IN_PROGRESS, COMPLETED.
                                {' '}<strong>Orientation:</strong> HORIZONTAL, VERTICAL, DIAGONAL_DOWN, DIAGONAL_UP.
                            </p>
                        </div>

                        <div className="design-section">
                            <h2>Pseudocode</h2>
                            <pre className="design-code-block"><code>{battleshipDesignCode}</code></pre>
                        </div>

                        <div className="design-section">
                            <h2>Important Design Decisions</h2>
                            <ol>
                                <li><strong>Why separate Game and GameController?</strong> The controller manages multiple games; each game manages its own rules and state.</li>
                                <li><strong>Why have a BUILDING state?</strong> Players must place their ships before they can attack.</li>
                                <li><strong>Who validates moves?</strong> The controller checks that the game exists and routes requests; the game checks player identity, turn, and game state; the board checks coordinates, repeated attacks, and ship placement.</li>
                                <li><strong>How do we support multiple games?</strong> Store games in a <code>Map&lt;GameId, Game&gt;</code> and route every request using its game ID.</li>
                                <li><strong>What about draws?</strong> With strictly alternating turns and immediate victory detection, a draw cannot naturally occur. If simultaneous attacks are required, resolve both attacks before deciding the outcome.</li>
                            </ol>
                        </div>
                    </div>

                    <div className="system-designs-footer-note">
                        When navy guys sense danger, they dont get "fight or flight" response. They get "fight or fleet" response.<br></br>
                        Gosh. I did the world a favor by not becoming a standup comedian.
                    </div>

                    <button className="back-btn" onClick={onBackClick}>Back to Designs</button>
                </section>
            </main>

            <footer className="footer">
                <p>© 2026 StupidDesigns.com. All rights reserved. Not that anyone actually wants them.</p>
            </footer>
        </div>
    );
}

export default BattleshipDesign;
